import { type Event as DeviceEvent } from '@eppendorf/vnls-notifications-utils';
import { DotsLoader } from '@eppendorf/vnls-react-components';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LoadingErrorHint } from '$components/loading-error-hint/loading-error-hint';
import { EventRow } from '$features/monitoring/events/components/event-row';
import styles from '$features/monitoring/events/monitoring-events.module.scss';
import {
  EventSeverityFilter,
  SeverityFilter,
} from '$features/report-hub/stability-report/severity-filter';
import { useGetStabilityReportEvents } from '$features/report-hub/stability-report/stability-report-events.api';

export interface StabilityReportEventsProps {
  reportId: string;
  initialSeverity?: EventSeverityFilter;
}

const getSeverityTags = (severity: EventSeverityFilter): string[] => {
  switch (severity) {
    case EventSeverityFilter.Info:
      return ['info', 'notice'];
    case EventSeverityFilter.Warning:
      return ['warning', 'warn'];
    case EventSeverityFilter.Error:
      return ['error'];
    case EventSeverityFilter.Alert:
      return ['alert'];
    default:
      return [];
  }
};

const eventMatchesSeverity = (
  event: Pick<DeviceEvent, 'tags'>,
  severity: EventSeverityFilter,
) => {
  if (severity === EventSeverityFilter.All) {
    return true;
  }

  const tags = (event.tags ?? []).map((tag) => tag.toLowerCase());
  const severityTags = getSeverityTags(severity);

  return severityTags.some((severityTag) => tags.includes(severityTag));
};

export function StabilityReportEvents({
  reportId,
  initialSeverity,
}: StabilityReportEventsProps) {
  const { t } = useTranslation();
  const { data: events = [], isLoading, isError } = useGetStabilityReportEvents(reportId);
  const [selectedSeverity, setSelectedSeverity] = useState<EventSeverityFilter>(
    initialSeverity ?? EventSeverityFilter.All,
  );

  useEffect(() => {
    if (initialSeverity) {
      setSelectedSeverity(initialSeverity);
    }
  }, [initialSeverity]);

  const filteredEvents = useMemo(
    () => events.filter((event) => eventMatchesSeverity(event, selectedSeverity)),
    [events, selectedSeverity],
  );

  if (isLoading) {
    return <DotsLoader className="m-top-10xl m-bottom-10xl" />;
  }

  return (
    <>
      <SeverityFilter
        selectedSeverity={selectedSeverity}
        onSeverityChange={setSelectedSeverity}
      />
      <div className={styles.deviceEventHeader}>
        <div>{t('monitoringDetail.eventsDetail.type')}</div>
        <div>{t('monitoringDetail.eventsDetail.time')}</div>
        <div>{t('monitoringDetail.eventsDetail.event')}</div>
      </div>
      <div className="device-event__list">
        {filteredEvents.length === 0 && !isError ? (
          <div className="flex flex__ai--center flex__jc--center font-bold font-size-l p-xxl">
            {t('stabilityDetail.noEventsWithinTimeRange')}
          </div>
        ) : (
          filteredEvents.map((deviceEvent) => (
            <EventRow key={deviceEvent.sourceEventId} deviceEvent={deviceEvent} />
          ))
        )}
      </div>
      {isError && <LoadingErrorHint className="box p-s m-top-m" />}
    </>
  );
}
